import { useCallback, useRef } from 'react';
import type { DragInfo } from '../types';
import { useDragOverlayContext } from '../app/GameProviders';
import { CONSTANTS } from '../data.js';

export function useInventoryDrag(params: {
  onDragEnd: (event: MouseEvent | TouchEvent | PointerEvent, info: DragInfo, itemId: string) => void;
}) {
  const { onDragEnd } = params;
  const { setInventoryOverlay, inventoryOverlayRef } = useDragOverlayContext();
  const rafId = useRef(0);

  const startInventoryCopyDrag = useCallback((e: React.PointerEvent, itemId: string) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();

    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    // Set state once to make overlay active and set initial position
    setInventoryOverlay({
      active: true,
      itemId,
      x: e.clientX,
      y: e.clientY,
    });

    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      // On move, update style directly via ref, throttled by rAF
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        if (!inventoryOverlayRef.current) {
          throw new Error('useInventoryDrag: inventoryOverlayRef is null during drag');
        }
        const size = CONSTANTS.WORKBENCH_ITEM_SIZE;
        const tx = Math.round(ev.clientX - size / 2);
        const ty = Math.round(ev.clientY - size / 2);
        inventoryOverlayRef.current.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      });
    };

    const finish = (ev: PointerEvent) => {
      ev.preventDefault();
      cancelAnimationFrame(rafId.current);

      const info: DragInfo = {
        point: { x: ev.clientX, y: ev.clientY },
        offset: { x: 0, y: 0 } // Offset is not used by inventory drag
      };
      onDragEnd(ev, info, itemId);

      setInventoryOverlay({ active: false, itemId: null, x: 0, y: 0 });

      document.body.style.userSelect = prevUserSelect;

      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', finish, { passive: false });
    window.addEventListener('pointercancel', finish, { passive: false });
  }, [onDragEnd, setInventoryOverlay, inventoryOverlayRef]);

  return { startInventoryCopyDrag };
}
